import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import {
  fill as fillPDFProvincialGr78,
  SCHEMA_TO_PDF_FIELD,
  PAGE_INDEX_FOR_SCHEMA_FIELD,
  STEP_ANCHOR_FIELDS,
} from "./fill.js";
import templateUrl from "./template.pdf";
import { PRESETS } from "./presets.js";
import { formSchema, defaultValues } from "./schema.js";
import {
  selectItems,
  twoPlans,
  threePlans,
  fourPlans,
  frenchPlans,
  ersCompleted as ersCompletedOpts,
} from "./options.js";
import { STEPS, STEP_FIELDS, TOP_STEPS, SUB_STEPS, ARTS_ROWS } from "./steps.js";
import { useReportForm } from "@/reports/shared_utils/useReportForm";
import { PillSelector } from "@/reports/report_components/PillSelector";
import { SectionHeading } from "@/reports/report_components/SectionHeading";
import { AICommentButton } from "@/reports/report_components/AICommentButton";
import {
  LSWH_CONFIG,
  LANGUAGE_CONFIG,
  NATIVE_LANGUAGE_CONFIG,
  FRENCH_CONFIG,
  MATHEMATICS_CONFIG,
  SCIENCE_CONFIG,
  HISTORY_CONFIG,
  GEOGRAPHY_CONFIG,
  HPPE_CONFIG,
  ARTS_CONFIG,
  OPTIONAL_STUDIES_CONFIG,
} from "./aiConfig";
import { FormFieldGrid } from "@/reports/report_components/FormFieldGrid";
import { StepIndicator } from "@/reports/report_components/StepIndicator";
import { ReportFormShell } from "@/reports/report_components/ReportFormShell";

// ─── Reusable primitives ──────────────────────────────────────────────────────

function GradeColumn({ control, gradeField, medianField, termLabel }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{termLabel}</span>
      <FormField
        control={control}
        name={gradeField}
        render={({ field }) => (
          <FormItem className="flex flex-col items-center">
            <span className="text-[8px] text-muted-foreground uppercase tracking-wide">Grade</span>
            <FormControl>
              <input
                {...field}
                maxLength={2}
                className="w-12 h-10 text-center text-lg font-bold border-2 border-border rounded-xl bg-input focus:border-primary focus:outline-none transition-colors"
              />
            </FormControl>
          </FormItem>
        )}
      />
      {medianField && (
        <FormField
          control={control}
          name={medianField}
          render={({ field }) => (
            <FormItem className="flex flex-col items-center">
              <span className="text-[8px] text-muted-foreground uppercase tracking-wide">Median</span>
              <FormControl>
                <input
                  {...field}
                  maxLength={2}
                  className="w-12 h-9 text-center text-base font-semibold border border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors"
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </div>
  );
}

function SubjectCard({ title, planField, grade1Field, median1Field, grade2Field, median2Field, commentsField, plans, nameField, control, aiConfig, form, reportId }) {
  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-muted/50 border-b border-border flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">{title}</span>
        {nameField && (
          <FormField
            control={control}
            name={nameField}
            render={({ field }) => (
              <FormItem className="mb-0">
                <FormControl>
                  <input
                    {...field}
                    placeholder="Subject name"
                    className="text-sm border border-border rounded-lg px-2 py-1 bg-input focus:border-primary focus:outline-none w-40"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </div>
      <div className="p-5 space-y-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Program Plan</p>
          <FormField
            control={control}
            name={planField}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PillSelector size="sm" value={field.value} onChange={field.onChange} options={plans} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        {grade1Field && grade2Field && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Term Grades & Medians</p>
            <div className="flex items-start gap-4">
              <GradeColumn control={control} gradeField={grade1Field} medianField={median1Field} termLabel="Term 1" />
              <div className="w-4 h-px bg-muted mt-8" />
              <GradeColumn control={control} gradeField={grade2Field} medianField={median2Field} termLabel="Term 2" />
            </div>
          </div>
        )}
        {commentsField && (
          aiConfig && form ? (
            <AICommentButton
              config={aiConfig}
              form={form}
              fieldName={commentsField}
              reportId={reportId}
              label="Comments"
              labelClassName="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-2"
              textareaClassName="min-h-[90px] resize-none text-sm"
              buttonClassName="border-primary/20 text-primary hover:bg-primary/5"
            />
          ) : (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Comments</p>
              <FormField
                control={control}
                name={commentsField}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="Teacher comments…" />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          )
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const FORM_CONFIG = {
  formSchema,
  defaultValues,
  fillPDF: fillPDFProvincialGr78,
  templateUrl,
  STEPS,
  STEP_FIELDS,
  STEP_ANCHOR_FIELDS,
  SCHEMA_TO_PDF_FIELD,
  getPageIndex: (f) => PAGE_INDEX_FOR_SCHEMA_FIELD[f] ?? 0,
  getInitialPage: (s) => s <= 3 ? 1 : s <= 7 ? 2 : 3,
};

export const FreeTrialReportCardProvincialGr78202309 = ({ reportId, backUrl, contextInfo, defaultsGroupId, onRequestSave, readOnly = false } = {}) => {
  const hook = useReportForm(FORM_CONFIG, { reportId, defaultsGroupId, groupGrade: contextInfo?.grade });

  const renderStep = (form, currentStep) => {
    const { control } = form;

    if (currentStep === 1) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Student Information" />
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
            <div className="grid grid-cols-2 gap-4">
              {[
                { name: "studentName",      label: "Student Name",       colSpan: 2 },
                { name: "oen",              label: "OEN"                            },
                { name: "date",             label: "Date"                           },
                { name: "grade",            label: "Grade",              small: true },
                { name: "gradeInSeptember", label: "Grade in Sept.",     small: true },
                { name: "teacher",          label: "Teacher",            colSpan: 2 },
              ].map(({ name, label, colSpan, small }) => (
                <FormField
                  key={name}
                  control={control}
                  name={name}
                  render={({ field }) => (
                    <FormItem className={colSpan === 2 ? "col-span-2" : ""}>
                      <FormLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</FormLabel>
                      <FormControl>
                        {small ? (
                          <input {...field} maxLength={3} className="w-16 h-10 text-center text-base font-bold border-2 border-border rounded-xl bg-input focus:border-primary focus:outline-none transition-colors block" />
                        ) : (
                          <Input {...field} />
                        )}
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className="space-y-5">
          <SectionHeading title="School & Attendance" />
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">School Details</p>
            <FormFieldGrid
              fields={[
                { name: "board",     label: "Board",          colSpan: 2 },
                { name: "school",    label: "School",         colSpan: 2 },
                { name: "address1",  label: "Address Line 1", colSpan: 2 },
                { name: "address2",  label: "Address Line 2", colSpan: 2 },
                { name: "principal", label: "Principal"                   },
                { name: "telephone", label: "Telephone"                   },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-muted-foreground uppercase tracking-wide"
            />
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-4">Attendance</p>
            <FormFieldGrid
              fields={[
                { name: "daysAbsent",      label: "Days Absent (Term)", inputMode: "numeric" },
                { name: "totalDaysAbsent", label: "Total Days Absent",  inputMode: "numeric" },
                { name: "timesLate",       label: "Times Late (Term)",  inputMode: "numeric" },
                { name: "totalTimesLate",  label: "Total Times Late",   inputMode: "numeric" },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-muted-foreground uppercase tracking-wide"
              gridClassName="grid grid-cols-2 gap-6"
            />
          </div>
        </div>
      );
    }

    if (currentStep === 3) {
      const SKILLS = [
        { key: "responsibility",  label: "Responsibility"   },
        { key: "organization",    label: "Organization"     },
        { key: "independentWork", label: "Independent Work" },
        { key: "collaboration",   label: "Collaboration"    },
        { key: "initiative",      label: "Initiative"       },
        { key: "selfRegulation",  label: "Self-Regulation"  },
      ];
      return (
        <div className="space-y-5">
          <SectionHeading title="Learning Skills & Work Habits" subtitle="Ratings for both terms" />
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="grid items-center border-b border-border bg-muted/50 px-5 py-2.5" style={{ gridTemplateColumns: "160px 1fr 1px 1fr" }}>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Skill</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-primary text-center">Term 1</span>
              <span />
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground text-center">Term 2</span>
            </div>
            {SKILLS.map(({ key, label }, i) => (
              <div key={key} className={`grid items-center px-5 py-3 ${i < SKILLS.length - 1 ? "border-b border-border" : ""}`} style={{ gridTemplateColumns: "160px 1fr 1px 1fr" }}>
                <span className="text-sm font-medium text-foreground">{label}</span>
                <FormField control={control} name={`${key}1`} render={({ field }) => (
                  <FormItem><FormControl><PillSelector size="sm" value={field.value} onChange={field.onChange} options={selectItems} /></FormControl></FormItem>
                )} />
                <div className="h-8 bg-muted" />
                <FormField control={control} name={`${key}2`} render={({ field }) => (
                  <FormItem><FormControl><PillSelector size="sm" value={field.value} onChange={field.onChange} options={selectItems} /></FormControl></FormItem>
                )} />
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
            <AICommentButton
              config={LSWH_CONFIG}
              form={form}
              fieldName="strengthsNextStepsLSWH"
              reportId={reportId}
              label="Strengths & Next Steps - Learning Skills"
              placeholder="Teacher comments on learning skills…"
              labelClassName="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-2"
              textareaClassName="min-h-[100px] resize-none text-sm"
              buttonClassName="border-primary/20 text-primary hover:bg-primary/5"
            />
          </div>
        </div>
      );
    }

    if (currentStep === 4) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Language" />
          <SubjectCard title="Language" planField="languagePlan" grade1Field="languageGrade1" median1Field="languageMedian1" grade2Field="languageGrade2" median2Field="languageMedian2" commentsField="languageComments" plans={twoPlans} control={control} aiConfig={LANGUAGE_CONFIG} form={form} reportId={reportId} />
          <SubjectCard title="Native Language" nameField="nativeLanguage" planField="nativeLanguagePlan" grade1Field="nativeLanguageGrade1" median1Field="nativeLanguageMedian1" grade2Field="nativeLanguageGrade2" median2Field="nativeLanguageMedian2" commentsField="nativeLanguageComments" plans={twoPlans} control={control} aiConfig={NATIVE_LANGUAGE_CONFIG} form={form} reportId={reportId} />
        </div>
      );
    }

    if (currentStep === 5) {
      const FRENCH_SKILLS = [
        { label: "Listening", planField: "frenchListeningPlan", g1: "frenchListeningGrade1", m1: "frenchListeningMedian1", g2: "frenchListeningGrade2", m2: "frenchListeningMedian2" },
        { label: "Speaking",  planField: "frenchSpeakingPlan",  g1: "frenchSpeakingGrade1",  m1: "frenchSpeakingMedian1",  g2: "frenchSpeakingGrade2",  m2: "frenchSpeakingMedian2"  },
        { label: "Reading",   planField: "frenchReadingPlan",   g1: "frenchReadingGrade1",   m1: "frenchReadingMedian1",   g2: "frenchReadingGrade2",   m2: "frenchReadingMedian2"   },
        { label: "Writing",   planField: "frenchWritingPlan",   g1: "frenchWritingGrade1",   m1: "frenchWritingMedian1",   g2: "frenchWritingGrade2",   m2: "frenchWritingMedian2"   },
      ];
      return (
        <div className="space-y-4">
          <SectionHeading title="French" />
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">French Program Type</p>
            <FormField control={control} name="frenchPlan" render={({ field }) => (
              <FormItem><FormControl><PillSelector size="sm" value={field.value} onChange={field.onChange} options={frenchPlans} /></FormControl></FormItem>
            )} />
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="grid border-b border-border bg-muted/50 px-5 py-2.5" style={{ gridTemplateColumns: "90px 1fr auto" }}>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Skill</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Plan (ESL/IEP)</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">T1 Grade / Med · T2 Grade / Med</span>
            </div>
            {FRENCH_SKILLS.map(({ label, planField, g1, m1, g2, m2 }, i) => (
              <div key={planField} className={`grid items-center gap-4 px-5 py-3 ${i < FRENCH_SKILLS.length - 1 ? "border-b border-border" : ""}`} style={{ gridTemplateColumns: "90px 1fr auto" }}>
                <span className="text-sm font-semibold text-foreground">{label}</span>
                <FormField control={control} name={planField} render={({ field }) => (
                  <FormItem><FormControl><PillSelector size="sm" value={field.value} onChange={field.onChange} options={twoPlans} /></FormControl></FormItem>
                )} />
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-[8px] font-bold text-primary uppercase tracking-widest">T1</span>
                    <FormField control={control} name={g1} render={({ field }) => (
                      <FormItem><span className="text-[7px] text-muted-foreground uppercase text-center block">Grade</span><FormControl><input {...field} maxLength={2} className="w-10 h-8 text-center text-sm font-bold border-2 border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl></FormItem>
                    )} />
                    <FormField control={control} name={m1} render={({ field }) => (
                      <FormItem><span className="text-[7px] text-muted-foreground uppercase text-center block">Med</span><FormControl><input {...field} maxLength={2} className="w-10 h-8 text-center text-sm font-semibold border border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl></FormItem>
                    )} />
                  </div>
                  <div className="w-px h-16 bg-muted mt-4" />
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-[8px] font-bold text-muted-foreground uppercase tracking-widest">T2</span>
                    <FormField control={control} name={g2} render={({ field }) => (
                      <FormItem><span className="text-[7px] text-muted-foreground uppercase text-center block">Grade</span><FormControl><input {...field} maxLength={2} className="w-10 h-8 text-center text-sm font-bold border-2 border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl></FormItem>
                    )} />
                    <FormField control={control} name={m2} render={({ field }) => (
                      <FormItem><span className="text-[7px] text-muted-foreground uppercase text-center block">Med</span><FormControl><input {...field} maxLength={2} className="w-10 h-8 text-center text-sm font-semibold border border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl></FormItem>
                    )} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
            <AICommentButton
              config={FRENCH_CONFIG}
              form={form}
              fieldName="frenchComments"
              reportId={reportId}
              label="Comments"
              labelClassName="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-2"
              textareaClassName="min-h-[90px] resize-none text-sm"
              buttonClassName="border-primary/20 text-primary hover:bg-primary/5"
            />
          </div>
        </div>
      );
    }

    if (currentStep === 6) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Mathematics" />
          <SubjectCard title="Mathematics" planField="mathematicsPlan" grade1Field="mathematicsGrade1" median1Field="mathematicsMedian1" grade2Field="mathematicsGrade2" median2Field="mathematicsMedian2" commentsField="mathematicsComments" plans={threePlans} control={control} aiConfig={MATHEMATICS_CONFIG} form={form} reportId={reportId} />
        </div>
      );
    }

    if (currentStep === 7) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Science & Technology" />
          <SubjectCard title="Science & Technology" planField="scienceAndTechnologyPlan" grade1Field="scienceAndTechnologyGrade1" median1Field="scienceAndTechnologyMedian1" grade2Field="scienceAndTechnologyGrade2" median2Field="scienceAndTechnologyMedian2" commentsField="scienceAndTechnologyComments" plans={threePlans} control={control} aiConfig={SCIENCE_CONFIG} form={form} reportId={reportId} />
        </div>
      );
    }

    if (currentStep === 8) {
      return (
        <div className="space-y-4">
          <SectionHeading title="History & Geography" />
          <SubjectCard title="History" planField="historyPlan" grade1Field="historyGrade1" median1Field="historyMedian1" grade2Field="historyGrade2" median2Field="historyMedian2" commentsField="historyComments" plans={fourPlans} control={control} aiConfig={HISTORY_CONFIG} form={form} reportId={reportId} />
          <SubjectCard title="Geography" planField="geographyPlan" grade1Field="geographyGrade1" median1Field="geographyMedian1" grade2Field="geographyGrade2" median2Field="geographyMedian2" commentsField="geographyComments" plans={fourPlans} control={control} aiConfig={GEOGRAPHY_CONFIG} form={form} reportId={reportId} />
        </div>
      );
    }

    if (currentStep === 9) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Health & Physical Education" />
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-muted/50 border-b border-border"><span className="text-sm font-semibold text-foreground">Health Education</span></div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Program Plan</p>
                <FormField control={control} name="healthEducationPlan" render={({ field }) => (
                  <FormItem><FormControl><PillSelector size="sm" value={field.value} onChange={field.onChange} options={threePlans} /></FormControl></FormItem>
                )} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Term Grades & Medians</p>
                <div className="flex items-start gap-4">
                  <GradeColumn control={control} gradeField="healthEducationGrade1" medianField="healthEducationMedian1" termLabel="Term 1" />
                  <div className="w-4 h-px bg-muted mt-8" />
                  <GradeColumn control={control} gradeField="healthEducationGrade2" medianField="healthEducationMedian2" termLabel="Term 2" />
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-muted/50 border-b border-border"><span className="text-sm font-semibold text-foreground">Physical Education</span></div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Program Plan</p>
                <FormField control={control} name="physicalEducationPlan" render={({ field }) => (
                  <FormItem><FormControl><PillSelector size="sm" value={field.value} onChange={field.onChange} options={threePlans} /></FormControl></FormItem>
                )} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Term Grades & Medians</p>
                <div className="flex items-start gap-4">
                  <GradeColumn control={control} gradeField="physicalEducationGrade1" medianField="physicalEducationMedian1" termLabel="Term 1" />
                  <div className="w-4 h-px bg-muted mt-8" />
                  <GradeColumn control={control} gradeField="physicalEducationGrade2" medianField="physicalEducationMedian2" termLabel="Term 2" />
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
            <AICommentButton
              config={HPPE_CONFIG}
              form={form}
              fieldName="healthAndPhysicalEducationComments"
              reportId={reportId}
              label="H&PE Comments"
              labelClassName="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-2"
              textareaClassName="min-h-[90px] resize-none text-sm"
              buttonClassName="border-primary/20 text-primary hover:bg-primary/5"
            />
          </div>
        </div>
      );
    }

    if (currentStep === 10) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Arts" />
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
            <FormField control={control} name="artsHeader" render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-2">Arts Header</FormLabel>
                <FormControl><Input {...field} placeholder="e.g. The Arts" className="max-w-xs" /></FormControl>
              </FormItem>
            )} />
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="grid border-b border-border bg-muted/50 px-5 py-2.5" style={{ gridTemplateColumns: "1fr 1fr auto" }}>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Subject / Plan</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Program Plan</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">T1 Gr/Med · T2 Gr/Med</span>
            </div>
            {ARTS_ROWS.map(({ nameField, planField, grade1Field, median1Field, grade2Field, median2Field, placeholder }, i) => (
              <div key={nameField} className={`grid items-center gap-4 px-5 py-3 ${i < ARTS_ROWS.length - 1 ? "border-b border-border" : ""}`} style={{ gridTemplateColumns: "1fr 1fr auto" }}>
                <FormField control={control} name={nameField} render={({ field }) => (
                  <FormItem><FormControl><input {...field} placeholder={placeholder} className="w-full text-sm border border-border rounded-lg px-2.5 py-1.5 bg-input focus:border-primary focus:outline-none" /></FormControl></FormItem>
                )} />
                <FormField control={control} name={planField} render={({ field }) => (
                  <FormItem><FormControl><PillSelector size="sm" value={field.value} onChange={field.onChange} options={fourPlans} /></FormControl></FormItem>
                )} />
                <div className="flex items-start gap-2">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-[7px] font-bold text-primary uppercase tracking-widest">T1</span>
                    <FormField control={control} name={grade1Field} render={({ field }) => (
                      <FormItem><span className="text-[6px] text-muted-foreground uppercase text-center block">Gr</span><FormControl><input {...field} maxLength={2} className="w-10 h-8 text-center text-sm font-bold border-2 border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl></FormItem>
                    )} />
                    <FormField control={control} name={median1Field} render={({ field }) => (
                      <FormItem><span className="text-[6px] text-muted-foreground uppercase text-center block">Med</span><FormControl><input {...field} maxLength={2} className="w-10 h-8 text-center text-sm font-semibold border border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl></FormItem>
                    )} />
                  </div>
                  <div className="w-px h-16 bg-muted mt-3" />
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-[7px] font-bold text-muted-foreground uppercase tracking-widest">T2</span>
                    <FormField control={control} name={grade2Field} render={({ field }) => (
                      <FormItem><span className="text-[6px] text-muted-foreground uppercase text-center block">Gr</span><FormControl><input {...field} maxLength={2} className="w-10 h-8 text-center text-sm font-bold border-2 border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl></FormItem>
                    )} />
                    <FormField control={control} name={median2Field} render={({ field }) => (
                      <FormItem><span className="text-[6px] text-muted-foreground uppercase text-center block">Med</span><FormControl><input {...field} maxLength={2} className="w-10 h-8 text-center text-sm font-semibold border border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl></FormItem>
                    )} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
            <AICommentButton
              config={ARTS_CONFIG}
              form={form}
              fieldName="artsComments"
              reportId={reportId}
              label="Arts Comments"
              labelClassName="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-2"
              textareaClassName="min-h-[90px] resize-none text-sm"
              buttonClassName="border-primary/20 text-primary hover:bg-primary/5"
            />
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-muted/50 border-b border-border"><span className="text-sm font-semibold text-foreground">Optional Studies</span></div>
            <div className="p-5 space-y-4">
              <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr auto" }}>
                <FormField control={control} name="optionalStudies" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-1">Subject Name</FormLabel>
                    <FormControl><Input {...field} placeholder="e.g. Islamic Studies" /></FormControl>
                  </FormItem>
                )} />
                <FormField control={control} name="optionalStudiesPlan" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-1">Program Plan</FormLabel>
                    <FormControl><PillSelector size="sm" value={field.value} onChange={field.onChange} options={fourPlans} /></FormControl>
                  </FormItem>
                )} />
                <div className="flex items-end gap-2 pb-0.5">
                  <FormField control={control} name="optionalStudiesGrade1" render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">T1</span>
                      <FormControl><input {...field} maxLength={2} className="w-11 h-9 text-center text-base font-bold border-2 border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={control} name="optionalStudiesGrade2" render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">T2</span>
                      <FormControl><input {...field} maxLength={2} className="w-11 h-9 text-center text-base font-bold border-2 border-border rounded-lg bg-input focus:border-primary focus:outline-none transition-colors" /></FormControl>
                    </FormItem>
                  )} />
                </div>
              </div>
              <AICommentButton
                config={OPTIONAL_STUDIES_CONFIG}
                form={form}
                fieldName="optionalStudiesComments"
                reportId={reportId}
                label="Comments"
                labelClassName="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-1"
                textareaClassName="min-h-[80px] resize-none text-sm"
                buttonClassName="border-primary/20 text-primary hover:bg-primary/5"
              />
            </div>
          </div>
        </div>
      );
    }

    if (currentStep === 11) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Early Reading Strategy" subtitle="Record ERS assessment completion status" />
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Assessment Completed</p>
            <FormField control={control} name="ersCompleted" render={({ field }) => (
              <FormItem><FormControl>
                <div className="flex gap-3">
                  {ersCompletedOpts.map((opt) => {
                    const isClear = opt.value === "";
                    const isSelected = opt.value === field.value;
                    return (
                      <button key={opt.value + opt.label} type="button" onClick={() => field.onChange(opt.value)}
                        className={`px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-colors cursor-pointer ${
                          isSelected
                            ? isClear ? "bg-[hsl(var(--neutral-700))] border-[hsl(var(--neutral-700))] text-white"
                              : opt.value === "Yes" ? "bg-success border-success text-success-foreground"
                              : opt.value === "No" ? "bg-destructive border-destructive text-destructive-foreground"
                              : "bg-[hsl(var(--neutral-500))] border-[hsl(var(--neutral-500))] text-white"
                            : "bg-input border-border text-muted-foreground hover:bg-accent"
                        }`}
                      >{isClear ? "×" : opt.label}</button>
                    );
                  })}
                </div>
              </FormControl></FormItem>
            )} />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <ReportFormShell
      hook={hook}
      title="Ontario Provincial Report Card - Gr. 7–8"
      reportId={reportId}
      backUrl={backUrl}
      contextInfo={contextInfo}
      defaultsGroupId={defaultsGroupId}
      onRequestSave={onRequestSave}
      STEPS={STEPS}
      PRESETS={PRESETS}
      defaultPageCount={4}
      StepIndicator={StepIndicator}
      stepIndicatorProps={{ steps: TOP_STEPS, subSteps: SUB_STEPS }}
      renderStep={renderStep}
      formContentClassName="flex-1 overflow-y-auto px-5 py-5 bg-muted/50"
      readOnly={readOnly}
    />
  );
};
